// src/pages/DashboardPage.tsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { ProfileView } from '@/components/dashboard/ProfileView';
import { HealthDashboard } from '@/components/dashboard/HealthDashboard';
import { HealthMetricsPage } from '@/components/dashboard/HealthMetricsPage';
import { CategoriesView } from '@/components/dashboard/CategoriesView';
import { MyBrain } from '@/components/dashboard/MyBrain';
import { ChatPage } from '@/components/dashboard/ChatPage';
import { PrescriptionPage } from '@/components/dashboard/PrescriptionPage';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileMenuToggle } from '@/components/layout/MobileMenuToggle';
import { PageTransition } from '@/components/ui/PageTransition';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

type DashboardView = 'health' | 'upload' | 'profile' | 'category-detail' | 'categories' | 'brain' | 'chat' | 'prescription';

interface DashboardPageProps {
  userId: string;
  userEmail: string;
  healId?: string;
  onLogout: () => void;
  currentDashboardView: DashboardView;
  selectedCategory: string | null;
  dashboardRefreshKey?: number;
  onNavigateToView: (view: DashboardView) => void;
  onCategoryClick: (categoryName: string) => void;
  onShowPreview?: (data: any, jobId: string) => void;
  isDemo?: boolean;
  isUploadModalOpen?: boolean;
}



export const DashboardPage: React.FC<DashboardPageProps> = ({
  userId,
  userEmail,
  healId,
  onLogout,
  currentDashboardView,
  selectedCategory,
  dashboardRefreshKey,
  onNavigateToView,
  onCategoryClick,
  onShowPreview,
  isDemo = false,
  isUploadModalOpen
}) => {
  const currentView = currentDashboardView;
  const queryClient = useQueryClient();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [prescriptionResetKey, setPrescriptionResetKey] = useState(0);

  // Check backend connectivity on mount
  useEffect(() => {
    const checkConnectivity = async () => {
      try {
        await apiService.checkHealth();
        setIsConnected(true);
      } catch (error) {
        setIsConnected(false);
      }
    };

    checkConnectivity();
    const interval = setInterval(checkConnectivity, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Handle delete modal open
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setConfirmText('');
    setDeleteError('');
  };

  // Handle delete all data
  const handleConfirmDelete = async () => {
    if (confirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const response = await apiService.deleteAllUserData();

      if (response.success) {

        setShowDeleteModal(false);

        // Invalidate all queries to refresh data without page reload
        queryClient.invalidateQueries({ queryKey: ['healthSummary'] });
        queryClient.invalidateQueries({ queryKey: ['categoryInsights'] });
        queryClient.invalidateQueries({ queryKey: ['userReports'] });

        // Navigate to health dashboard if not already there
        if (currentView !== 'health') {
          onNavigateToView('health');
        }
      } else {
        throw new Error(response.error?.message || 'Failed to delete data');
      }
    } catch (error: any) {
      console.error('Delete all data error:', error);
      setDeleteError(error.message || 'Failed to delete data');
      setIsDeleting(false);
    }
  };

  // Handle delete user account
  const handleDeleteAccount = async () => {
    try {
      const response = await apiService.deleteUserAccount();

      if (response.success) {


        // Clear all local storage
        localStorage.clear();
        sessionStorage.clear();

        // Wait a moment for user to see the message
        setTimeout(() => {
          // Call logout handler which should redirect to login
          onLogout();
        }, 1500);
      } else {
        throw new Error(response.error?.message || 'Failed to delete account');
      }
    } catch (error: any) {
      console.error('Delete account error:', error);
      throw error; // Re-throw so ProfileView can handle the error
    }
  };

  const handleNavigationClick = (view: DashboardView) => {
    // Invalidate queries when navigating to health dashboard to get fresh data
    if (view === 'health' && currentView !== 'health') {
      queryClient.invalidateQueries({ queryKey: ['healthSummary'] });
      queryClient.invalidateQueries({ queryKey: ['categoryInsights'] });
    }

    // Reset prescription page to grid view when navigating to it
    if (view === 'prescription') {
      setPrescriptionResetKey(prev => prev + 1);
    }

    onNavigateToView(view);
  };

  const handleCategoryClick = (categoryName: string) => {
    onCategoryClick(categoryName);
  };

  const handleBackToCategories = () => {
    // Navigate back to the appropriate view based on category type
    const insights = queryClient.getQueryData<any>(['categoryInsights']);
    const insightList = Array.isArray(insights) ? insights : (insights as any)?.insights || [];
    const currentCategory = insightList.find((i: any) => i.category_name === selectedCategory);

    onNavigateToView('categories');
  };

  const handleNavigateToCategories = () => {
    // Navigate to categories
    onNavigateToView('categories');
  };

  const handleNavigateToPrescription = () => {
    // Navigate to prescription tab
    setPrescriptionResetKey(prev => prev + 1);
    onNavigateToView('prescription');
  };

  const renderBreadcrumb = () => {
    const breadcrumbMap: Record<DashboardView, string> = {
      health: 'Health Dashboard',
      upload: 'Upload',
      profile: 'Profile',
      'category-detail': selectedCategory || 'Category Details',
      categories: 'Health Categories',
      brain: 'My Brain',
      chat: 'AI Chat',
      prescription: 'Prescription'
    };

    return (
      <div style={{
        padding: 'var(--space-4) var(--space-6)',
        borderBottom: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-surface)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-4)'
      }}>
        <p style={{
          fontFamily: 'var(--font-primary)',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-muted)',
          margin: 0
        }}>
          <span style={{ color: 'var(--color-primary)' }}>Home</span>
          {' > '}
          <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-primary)' }}>
            {breadcrumbMap[currentView]}
          </span>
        </p>

        {/* Right Side Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)'
        }}>
          {/* Connection Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            {isConnected ? (
              <>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-success)',
                  boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)'
                }} />
                <span className="connection-status-text" style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-secondary)',
                  fontWeight: 'var(--font-medium)'
                }}>Connected</span>
              </>
            ) : (
              <>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-error)',
                  boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)'
                }} />
                <span className="connection-status-text" style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-secondary)',
                  fontWeight: 'var(--font-medium)'
                }}>Disconnected</span>
              </>
            )}
          </div>

          {/* Delete All Data Button */}
          <button
            onClick={handleDeleteClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              border: '1px solid var(--color-error)',
              borderRadius: 'var(--radius)',
              color: 'var(--color-error)',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-error)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-error)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            <span className="delete-button-text">Delete All Data</span>
          </button>

          {/* Theme Toggle */}
          <ThemeToggle />
        </div>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      fontFamily: 'var(--font-primary)'
    }}>
      {/* Mobile Menu Toggle */}
      <MobileMenuToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      {/* Sidebar */}
      <Sidebar
        currentView={currentView}
        userEmail={userEmail}
        onNavigate={(view) => {
          handleNavigationClick(view);
          setIsSidebarOpen(false); // Close sidebar on navigation (mobile)
        }}
        onLogout={onLogout}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        onCollapsedChange={setIsSidebarCollapsed}
        onShowPreview={onShowPreview}
        onUploadComplete={() => {
          // Invalidate queries to refresh dashboard data
          queryClient.invalidateQueries({ queryKey: ['healthSummary'] });
          queryClient.invalidateQueries({ queryKey: ['categoryInsights'] });
          queryClient.invalidateQueries({ queryKey: ['userReports'] });
        }}
        hasData={!!(queryClient.getQueryData<any>(['healthSummary'])?.overall_health_score) || isDemo}
        isDemo={isDemo}
      />

      {/* Fixed Back Button - Outside PageTransition to avoid transform breaking position:fixed */}
      {currentView === 'category-detail' && (
        <button
          className="dashboard-back-button"
          onClick={handleBackToCategories}
          onMouseEnter={() => setIsBackButtonHovered(true)}
          onMouseLeave={() => setIsBackButtonHovered(false)}
          style={{
            position: 'fixed',
            top: '165px',
            left: isSidebarCollapsed ? '90px' : '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            color: isBackButtonHovered ? '#6366f1' : '#4a5568',
            cursor: 'pointer',
            borderRadius: '12px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: `1px solid ${isBackButtonHovered ? 'rgba(99, 102, 241, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
            boxShadow: isBackButtonHovered
              ? '0 6px 20px rgba(99, 102, 241, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
            zIndex: 1001,
            transform: isBackButtonHovered ? 'translateY(-2px) scale(1.05)' : 'none'
          }}
          title="Back to Categories"
        >
          <ChevronLeft
            size={24}
            style={{
              transition: 'transform 0.3s ease',
              transform: isBackButtonHovered ? 'translateX(-3px)' : 'none'
            }}
          />
        </button>
      )}

      {/* Main Content Area */}
      <main
        className="dashboard-main-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: isSidebarCollapsed ? '70px' : '280px',
          transition: 'margin-left 0.3s ease',
          minHeight: '100vh'
        }}
      >
        {/* Breadcrumb */}
        {renderBreadcrumb()}

        {/* Content */}
        <div>
          <PageTransition transitionKey={currentView}>
            {currentView === 'health' && (
              <HealthDashboard
                key={dashboardRefreshKey}
                onShowPreview={onShowPreview}
                onNavigateToCategories={handleNavigateToCategories}
                onNavigateToPrescription={handleNavigateToPrescription}
                isUploadModalOpen={isUploadModalOpen}
              />
            )}

            {currentView === 'category-detail' && selectedCategory && (
              <HealthMetricsPage
                categoryName={selectedCategory}
                onBack={handleBackToCategories}
                isDemo={isDemo}
              />
            )}

            {currentView === 'upload' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                padding: 'var(--space-6)',
                fontFamily: 'var(--font-primary)'
              }}>
                <div style={{
                  textAlign: 'center',
                  maxWidth: '500px'
                }}>
                  <h2 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--space-4)'
                  }}>
                    Upload your health reports
                  </h2>
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-6)'
                  }}>
                    Use the upload button on your Health Dashboard to upload lab reports and medical documents.
                  </p>
                  <button
                    onClick={() => onNavigateToView('health')}
                    style={{
                      padding: 'var(--space-3) var(--space-6)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius)',
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-medium)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Go to Health Dashboard
                  </button>
                </div>
              </div>
            )}

            {currentView === 'profile' && (
              <ProfileView
                userEmail={userEmail}
                userId={userId}
                healId={healId}
                onDeleteAccount={handleDeleteAccount}
              />
            )}

            {currentView === 'categories' && (
              <CategoriesView
                userId={userId}
                onCategoryClick={handleCategoryClick}
                isDemo={isDemo}
              />
            )}

            {currentView === 'brain' && (
              <MyBrain />
            )}

            {currentView === 'chat' && (
              <ChatPage />
            )}

            {currentView === 'prescription' && (
              <PrescriptionPage key={prescriptionResetKey} />
            )}

          </PageTransition>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontFamily: 'var(--font-primary)'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%',
            boxShadow: 'var(--shadow-2xl)',
            border: '2px solid #DC2626'
          }}>
            {/* Warning Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)'
            }}>
              <div style={{
                backgroundColor: '#FEE2E2',
                borderRadius: '50%',
                padding: 'var(--space-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <h2 style={{
                fontSize: 'var(--text-xl)',
                fontWeight: 'var(--font-bold)',
                color: '#DC2626',
                margin: 0
              }}>
                Delete All Health Data
              </h2>
            </div>

            {/* Warning Message */}
            <div style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #DC2626',
              borderRadius: 'var(--radius)',
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-4)'
            }}>
              <p style={{
                fontSize: 'var(--text-base)',
                color: '#991B1B',
                margin: 0,
                marginBottom: 'var(--space-2)',
                fontWeight: 'var(--font-semibold)'
              }}>
                ⚠️ WARNING: This action cannot be undone!
              </p>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: '#991B1B',
                margin: 0,
                lineHeight: 1.6
              }}>
                This will permanently delete all your health reports, metrics, insights, profiles, and chat conversations. Your account will remain active, but all health data will be lost forever.
              </p>
            </div>

            {/* Confirmation Input */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Type <strong style={{ color: '#DC2626' }}>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                disabled={isDeleting}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: `2px solid ${deleteError ? '#DC2626' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!deleteError) e.currentTarget.style.borderColor = '#DC2626';
                }}
                onBlur={(e) => {
                  if (!deleteError) e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              />
              {deleteError && (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: '#DC2626',
                  margin: 'var(--space-2) 0 0 0'
                }}>
                  {deleteError}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmText('');
                  setDeleteError('');
                }}
                disabled={isDeleting}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  opacity: isDeleting ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-background)';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting || confirmText !== 'DELETE'}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'white',
                  backgroundColor: '#DC2626',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  cursor: (isDeleting || confirmText !== 'DELETE') ? 'not-allowed' : 'pointer',
                  opacity: (isDeleting || confirmText !== 'DELETE') ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting && confirmText === 'DELETE') {
                    e.currentTarget.style.backgroundColor = '#B91C1C';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting && confirmText === 'DELETE') {
                    e.currentTarget.style.backgroundColor = '#DC2626';
                  }
                }}
              >
                {isDeleting ? (
                  <>
                    <span>Deleting...</span>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .dashboard-main-content {
            margin-left: 0 !important;
          }
          .dashboard-back-button {
            left: 16px !important;
            top: 16px !important;
          }
        }

        @media (max-width: 640px) {
          /* Hide "Delete All Data" text on small screens, show icon only */
          .delete-button-text {
            display: none;
          }
          /* Hide connection status text on small screens, show dot only */
          .connection-status-text {
            display: none;
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};